import { useTranslation } from "react-i18next";
import { buildLocalizedPath, type Lang } from "~/config/route-config";
import { SALES_ENABLED } from "~/lib/sales";
import { cn } from "~/lib/cn";
import { Button } from "~/components/ui";
import { useCartStore, getCartTotal } from "~/store/cartStore";

interface CartSidebarProps {
  className?: string;
}

export function CartSidebar({ className }: CartSidebarProps) {
  const { t, i18n } = useTranslation();
  const items = useCartStore((s) => s.items);
  const isOpen = useCartStore((s) => s.isCartOpen);
  const closeCart = useCartStore((s) => s.closeCart);
  const removeItem = useCartStore((s) => s.removeItem);

  const total = getCartTotal(items);
  const fmtPrice = (price: number) =>
    `${price.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;

  return (
    <div
      className={cn(
        "fixed top-0 right-0 h-full w-full md:w-[400px] bg-primary z-[101] transform transition-transform duration-300 border-l border-borderLight flex flex-col",
        isOpen ? "translate-x-0" : "translate-x-full",
        className,
      )}
      role="dialog"
      aria-modal="true"
      aria-label={t("cart.shopping_bag")}
      // Closed, the panel is only translated off-screen (the slide needs it mounted), so
      // `inert` takes it out of the tab order and the accessibility tree: Tab after the
      // cookie dialog landed on its «Cerrar» at x=1776 of a 1440 viewport (T872 QA-01).
      inert={isOpen ? undefined : true}
    >
      <div className="p-6 border-b border-borderLight flex justify-between items-center bg-primary">
        <span className="text-sm font-bold uppercase tracking-widest">
          {t("cart.your_bag")}
        </span>
        <Button
          type="button"
          variant="icon"
          onClick={closeCart}
          className="border-none text-2xl font-light hover:opacity-50 transition-opacity"
          aria-label={t("ui.close")}
        >
          &times;
        </Button>
      </div>

      <div className="flex-1 p-6 bg-surfaceMuted flex flex-col gap-6 overflow-y-auto">
        {items.length === 0 ? (
          <p className="italic text-xs font-light text-textSecondary">
            {t("cart.empty")}
          </p>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-4 border-b border-borderLight pb-4"
            >
              {item.imageUrl && (
                <div className="w-16 h-16 bg-surfaceMid border border-borderLight shrink-0">
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold uppercase tracking-widest truncate">
                  {item.name}
                </div>
                <div className="text-xs text-textSecondary mt-1">
                  {fmtPrice(item.price)}
                </div>
              </div>
              <Button
                type="button"
                variant="icon"
                onClick={() => removeItem(item.id)}
                className="border-none text-textSecondary hover:text-textMain transition-colors shrink-0"
                aria-label={t("cart.remove")}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </Button>
            </div>
          ))
        )}
      </div>

      <div className="p-6 border-t border-borderLight bg-primary">
        <div className="flex justify-between mb-6 text-sm font-bold uppercase tracking-widest">
          <span>{t("cart.total")}</span>
          <span>{fmtPrice(total)}</span>
        </div>
        {/* Two reasons this can be inert, and they are different (T680). Sales are not
            open, so there is nothing to check out; and the bag can only be empty. The
            href is corrected anyway rather than left as the unlocalised `/checkout` it
            was — that literal 404s, and leaving it for whoever flips SALES_ENABLED would
            hand them the same trap this was found by. */}
        {SALES_ENABLED && items.length > 0 ? (
          <Button
            size="sm"
            className="w-full"
            href={buildLocalizedPath("checkout", (i18n.language === "en" ? "en" : "es") as Lang)}
          >
            {t("cart.checkout")}
          </Button>
        ) : (
          <Button size="sm" className="w-full" disabled>
            {t("cart.checkout")}
          </Button>
        )}
      </div>
    </div>
  );
}
